<template>
  <form-text
    type="password"
    :model-value="modelValue"
    :label="`${i18n.t('frontend/password')}:*`"
    :input-id="'register__password'"
    :debounce="200"
    autocomplete="new-password"
    @update:model-value="handleInput"
  >
    <template #hint>
      <p
        v-if="errorMessage != null"
        class="input-text__hint fs-tiny mb-0 mt-2 text-danger"
      >
        {{ i18n.t(errorMessage, { field: i18n.t('frontend/password') }) }}
      </p>
      <p
        v-else-if="passwordStrengthInfo"
        class="input-text__hint fs-tiny mb-0 mt-2"
        :class="[`text-${passwordStrengthInfo.class}`]"
      >
        <fa-icon
          v-if="passwordStrengthInfo.strength === 'strong'"
          :icon="faCheck"
        />
        <fa-icon v-else :icon="faCircleExclamation" class="me-2" />
        {{
          i18n.t('register/form/password/strength_hint', {
            strength: toTitleCase(
              i18n
                .t(
                  `register/form/password/strength/${passwordStrengthInfo.strength}`,
                )
                .toString(),
            ),
          })
        }}
      </p>
      <p v-else class="input-text__hint fs-tiny mb-0 mt-2 text-rhino">
        {{ i18n.t('register/form/password/hint') }}
      </p>
    </template>
  </form-text>
</template>

<script lang="ts">
import { useThrottleFn } from '@vueuse/shared';
import toTitleCase from '~/helpers/toTitleCase';
import { faCheck, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

export default defineComponent({
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    errorMessage: {
      type: String,
      default: null,
    },
  },
  emits: ['update:modelValue'],
  setup(_, { emit }) {
    const i18n = useI18n();

    // password strength handler
    let zxcvbn = null;
    const passwordStrengthScore = ref<number>(-1);
    const passwordStrengthInfo = computed<{
      class: 'danger' | 'warning' | 'success';
      strength: 'weak' | 'medium' | 'strong';
    }>(() => {
      switch (passwordStrengthScore.value) {
        case 0:
          return {
            class: 'danger',
            strength: 'weak',
          };
        case 1:
          return {
            class: 'danger',
            strength: 'weak',
          };
        case 2:
          return {
            class: 'warning',
            strength: 'medium',
          };
        case 3:
          return {
            class: 'success',
            strength: 'strong',
          };
        case 4:
          return {
            class: 'success',
            strength: 'strong',
          };

        default:
          return null;
      }
    });

    const handleInput = useThrottleFn((value) => {
      const { score } = zxcvbn(value);
      passwordStrengthScore.value = score;

      emit('update:modelValue', value);
    }, 100);

    onMounted(async () => {
      // lazy load zxcvbn on client side in order not to bloat the client
      const zxcvbnModule = await import('zxcvbn');
      zxcvbn = zxcvbnModule.default;
    });

    return {
      faCheck,
      faCircleExclamation,

      i18n,
      passwordStrengthInfo,
      toTitleCase,
      handleInput,
    };
  },
});
</script>

<style scoped></style>
